import Styled, { css } from 'styled-components';

import { FONT_MAP, media } from '../../../lib/StyledComponents/StyledComponents';
import { cssDeclaration, getNumValFromStr, REM_NUM_CONVERSION } from '../Utils';

import { IDisabledProps, IHoveredProps, IInputError, IInputMaxWidth, IReadOnlyProps } from './common';
import { StyledSelectInput } from './SelectInput/SelectInputStyles';

interface IFormErrorProps {
  canIndentError?: boolean;
  hasInlineDisplay?: boolean;
}

type InputControlStyleProps = IDisabledProps & IReadOnlyProps & IInputError & IHoveredProps;

export const FormError = Styled.small<IFormErrorProps>`
  width: 100%;
  line-height: 1.5;
  /*
    zeros global margin set by src/lib/StyledComponents/globalStyleCommon.ts
    this can be removed once the global css is restructured
  */
  margin: 0;

  ${({ theme: { themeTwo }, hasInlineDisplay, canIndentError }) => {
    const { global, form: { errorText, checkbox }} = themeTwo;
    const errorOffset = global.edgeSize[checkbox.size];
    const errorOffsetVal = getNumValFromStr(errorOffset) * REM_NUM_CONVERSION; // value needs to be pxs to offset correctly
    /*
      this styles for partners who's error msgs are indented to align with label text
      8px is the non themed margin right from InputCheckboxStyles.ts:84
    */
    const marginCalc = !!checkbox.margin.right ? themeTwo.global.edgeSize[checkbox.margin.right] || checkbox.margin.right : '8px';

    return css`
      font-family: ${errorText.font.family && FONT_MAP[errorText.font.family]};
      line-height: ${errorText.font.lineHeight && errorText.font.lineHeight};

      /* color */ ${cssDeclaration(themeTwo, 'color', errorText.font.color)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', errorText.font.size)};
      /* margin */ ${cssDeclaration(themeTwo, 'margin', errorText.margin)};

      ${errorText.checkboxAndRadioErrorIndention && canIndentError && !hasInlineDisplay && css`
        margin-left: calc(${errorOffsetVal}px + ${marginCalc} + 1px);
      `}
    `;
  }}
  `;

export const FormHelperTxt = Styled.span<InputControlStyleProps & { canIndentHelperText?: boolean }>`
  display: inline-block;
  line-height: 1.5;


  ${({ theme: { themeTwo }, canIndentHelperText, isDisabled, isHovered, isReadOnly, error }) => {
    const { global, form: { checkbox, helperText, errorText }} = themeTwo;

    const helperTextOffset = global.edgeSize[checkbox.size];
    const helperTextOffsetVal = getNumValFromStr(helperTextOffset) * REM_NUM_CONVERSION;
     /*
      this styles for partners who's helper text msgs are indented to align with label text
      8px is the non themed margin right from InputCheckboxStyles.ts:84
    */
    const marginCalc = !!checkbox.margin.right ? themeTwo.global.edgeSize[checkbox.margin.right] || checkbox.margin.right : '8px';

    return css`
      font-weight: ${helperText.font.weight};
      font-family: ${helperText.font.family && FONT_MAP[helperText.font.family]};
      line-height: ${helperText.font.lineHeight && helperText.font.lineHeight};

      /* color */ ${cssDeclaration(themeTwo, 'color', helperText.font.color.default)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', helperText.font.size)};
      /* margin */ ${cssDeclaration(themeTwo, 'margin', helperText.margin)};

      ${helperText.font.color.hover && isHovered && css`
        transition: color 0.1s ease-out;
        /* color */ ${cssDeclaration(themeTwo, 'color', helperText.font.color.hover)};
      `}

      ${isDisabled && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', helperText.font.color.disabled)};
      `}

      ${error && css`
        /* color */ ${helperText.font.color.error && cssDeclaration(themeTwo, 'color', helperText.font.color.error)};

      `}

      ${isReadOnly && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', helperText.font.color.readOnly)};
      `}

      ${errorText.checkboxAndRadioErrorIndention && canIndentHelperText && css`
        margin-left: calc(${helperTextOffsetVal}px + ${marginCalc} + 1px);
      `}
    `;
  }};
`;

export const FormLegend = Styled.legend`
  ${({ theme: { themeTwo }}) => css`
    font-weight: ${themeTwo.form.legend.font.weight};
    font-family: ${themeTwo.form.legend.font.family && FONT_MAP[themeTwo.form.legend.font.family]};
    line-height: ${themeTwo.form.legend.font.lineHeight};

    /* color */ ${cssDeclaration(themeTwo, 'color', themeTwo.form.legend.font.color)};
    /* font-size */ ${cssDeclaration(themeTwo, 'font-size', themeTwo.form.legend.font.size)};
    /* margin */ ${cssDeclaration(themeTwo, 'margin', themeTwo.form.legend.margin)};
  `}
`;

export const FormLabel = Styled.label<InputControlStyleProps>`
  display: block;

  ${({ theme, isDisabled, isHovered, isReadOnly, error }) => {
    const themeTwo = theme.themeTwo;
    const { label } = theme.themeTwo.form;

    return css`
      width: fit-content;
      font-weight: ${label.font.weight};
      font-family: ${label.font.family && FONT_MAP[label.font.family]};

      /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.default)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', label.font.size)};
      /* line-height */ ${label.font.lineHeight && cssDeclaration(themeTwo, 'line-height', label.font.lineHeight)};
      /* margin */ ${cssDeclaration(themeTwo, 'margin', label.margin)};

      ${label.font.color.hover && isHovered && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.hover)};
        transition: color 0.1s ease-out;
      `}

      ${isDisabled && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.disabled)};

        &.hover {
          transition: color 0.1s ease-out;
          /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.disabled)};
        }

        &:hover {
          /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.disabled)};
        }
      `}

      ${error && css`
        /* color */ ${label.font.color.error && cssDeclaration(themeTwo, 'color', label.font.color.error)};
      `}

      ${isReadOnly && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.readOnly)};

        &.hover {
          /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.readOnly)};
          transition: color 0.1s ease-out;
        }

        &:hover {
          /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.readOnly)};
        }
      `}

      // this allows for tentant specific styles to be added through the theme
      ${label.additionalStyles && label.additionalStyles}
    `;
  }}
`;

export const FormGroup = Styled.div<IInputMaxWidth>`
  flex: 0 1 100%;

  /* ios9 select/option fix */
  &:last-of-type ${StyledSelectInput} {
    margin-right: 0;
  }

  ${({ maxWidth }) => css`
    ${maxWidth && css`
      ${media.small`
        max-width: ${maxWidth};
      `}
    `}
  `}
`;
